/**
 * Dialog 'Tipos de recurso': lista (nome + Switch ativo + aviso de garantia
 * e-mail/dias via PUT no blur) e form inline nome + e-mail + dias → POST.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { EquipmentType } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { equipmentTypesOptions, errorMessage } from './resources-queries';
import { useCreateEquipmentType, useUpdateEquipmentType } from './resources-mutations';

export function ResourceTypesDialog({ onClose }: { onClose: () => void }) {
  const typesQ = useQuery(equipmentTypesOptions());
  const create = useCreateEquipmentType();
  const update = useUpdateEquipmentType();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [days, setDays] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate(
      {
        name: name.trim(),
        warrantyNotifyEmail: email.trim() || null,
        warrantyNotifyDaysBefore: days !== '' ? Number(days) : null,
      },
      {
        onSuccess: () => {
          setName('');
          setEmail('');
          setDays('');
        },
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title="Tipos de recurso" className="sm:max-w-xl">
      <p className="mb-3 text-[13px] text-muted-foreground">
        Aviso de garantia por tipo: e-mail que recebe a notificação e dias de antecedência.
      </p>
      {typesQ.isPending ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : typesQ.isError ? (
        <p className="text-sm text-destructive">
          Erro ao carregar tipos: {errorMessage(typesQ.error)}
        </p>
      ) : typesQ.data.length === 0 ? (
        <EmptyState className="h-28" message="Nenhum tipo de recurso cadastrado" />
      ) : (
        <div className="rounded-md border">
          {typesQ.data.map((t) => (
            <TypeRow
              key={t.id}
              type={t}
              pending={update.isPending}
              onSave={(body) => update.mutate({ id: t.id, body })}
            />
          ))}
        </div>
      )}
      {update.error ? (
        <p className="mt-2 text-sm text-destructive">
          Erro ao atualizar tipo: {errorMessage(update.error)}
        </p>
      ) : null}

      {/* Form inline: novo tipo */}
      <form onSubmit={submit} className="mt-4 border-t border-divider pt-4">
        <Label className="text-[13px]">Novo tipo</Label>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do tipo…"
            aria-label="Nome do tipo de recurso"
            className="h-8 min-w-36 flex-1 text-sm"
          />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail de aviso"
            aria-label="E-mail de aviso de garantia"
            className="h-8 w-48 text-sm"
          />
          <Input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            placeholder="Dias"
            aria-label="Dias de antecedência do aviso"
            className="h-8 w-20 text-sm tabular-nums"
          />
          <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
            <Plus />
            Adicionar
          </Button>
        </div>
        {create.error ? (
          <p className="mt-2 text-sm text-destructive">
            Erro ao criar tipo: {errorMessage(create.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function TypeRow({
  type,
  pending,
  onSave,
}: {
  type: EquipmentType;
  pending: boolean;
  onSave: (body: {
    name: string;
    warrantyNotifyEmail: string | null;
    warrantyNotifyDaysBefore: number | null;
    active: boolean;
  }) => void;
}) {
  const [email, setEmail] = useState(type.warrantyNotifyEmail ?? '');
  const [days, setDays] = useState(
    type.warrantyNotifyDaysBefore != null ? String(type.warrantyNotifyDaysBefore) : ''
  );

  const body = (active: boolean) => ({
    name: type.name,
    warrantyNotifyEmail: email.trim() || null,
    warrantyNotifyDaysBefore: days !== '' ? Number(days) : null,
    active,
  });

  const warrantyDirty =
    (email.trim() || null) !== (type.warrantyNotifyEmail ?? null) ||
    (days !== '' ? Number(days) : null) !== (type.warrantyNotifyDaysBefore ?? null);

  const saveWarranty = () => {
    if (warrantyDirty) onSave(body(type.active));
  };

  return (
    <div className="flex h-11 items-center gap-2.5 border-b border-divider px-3 last:border-0">
      <span className="min-w-0 flex-1 truncate text-sm font-medium" title={type.name}>
        {type.name}
      </span>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={saveWarranty}
        placeholder="E-mail de aviso"
        aria-label={`E-mail de aviso de garantia de ${type.name}`}
        className="h-7 w-44 text-[13px]"
      />
      <Input
        type="number"
        min={1}
        max={365}
        value={days}
        onChange={(e) => setDays(e.target.value)}
        onBlur={saveWarranty}
        placeholder="Dias"
        aria-label={`Dias de antecedência do aviso de ${type.name}`}
        className="h-7 w-16 text-[13px] tabular-nums"
      />
      <Switch
        checked={type.active}
        disabled={pending}
        onCheckedChange={(checked) => onSave(body(checked))}
        aria-label={type.active ? `Inativar ${type.name}` : `Ativar ${type.name}`}
      />
    </div>
  );
}
